import { faSortAsc } from "@fortawesome/pro-solid-svg-icons/faSortAsc";
import { faSortDesc } from "@fortawesome/pro-solid-svg-icons/faSortDesc";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Combobox, ComboboxOption } from "~/components/ComboboxV2";
import { Button } from "~/components/ui/button";
import { useDebouncedState } from "~/utils/useDebouncedInput";
import { FilterMetadata, SortDirections, SortQuery } from "../types";
import { cn } from "~/lib/utils";

// // // //

// Debounce sort changes so rapid field/direction toggles don't fire a
// refetch per click - the trigger updates instantly, the API call trails it.
const SORT_DEBOUNCE_MS = 1200;

// NOTE - a field with no direction isn't sortable in the API, so selecting a
// field from a cleared state has to pick one. Descending matches how these
// fields are almost always read (largest AUM / most recent date first).
const DEFAULT_SORT_DIRECTION = SortDirections.desc;

const CLEARED_SORT: SortQuery = {
    field: "",
    direction: SortDirections.none,
};

interface Props {
    filterMetadata: FilterMetadata[];
    sortInput: SortQuery;
    onChange: (updatedSort: SortQuery) => void;
    loading?: boolean;
    className?: string;
}

/**
 * Field + direction control for an ApiQuery's `sort`. Only the fields whose
 * FilterMetadata is flagged `sortable` are offered - if the endpoint's metadata
 * has none, this renders nothing at all.
 */
export function ApiSortInput(props: Props) {
    const {
        filterMetadata,
        sortInput,
        onChange,
        loading = false,
        className,
    } = props;

    const [internalSort, setInternalSort] = useDebouncedState<SortQuery>({
        value: sortInput,
        debounceTimeout: SORT_DEBOUNCE_MS,
        onChange,
    });

    const sortableMetadata = filterMetadata.filter(
        (metadata) => metadata.sortable === true,
    );

    // Nothing on this endpoint can be sorted - there's no control to render
    if (sortableMetadata.length === 0) {
        return null;
    }

    const options: ComboboxOption[] = sortableMetadata.map((metadata) => ({
        value: metadata.field,
        label: metadata.content.label,
        description: metadata.content.description,
    }));

    // Guard against a sort field that isn't in this endpoint's sortable
    // metadata (i.e. a query built elsewhere) - show it as unselected rather
    // than as a blank trigger
    const selectedField =
        options.find((option) => option.value === internalSort.field)?.value ??
        null;

    const isDescending = internalSort.direction === SortDirections.desc;

    return (
        <div className={cn("flex items-center gap-2", className)}>
            <span className="text-sm whitespace-nowrap text-muted-foreground">
                Sort by
            </span>

            <div className="w-56">
                <Combobox
                    size="sm"
                    itemSize="sm"
                    value={selectedField}
                    options={options}
                    showClear
                    searchFields="all"
                    placeholder="Select a field..."
                    searchPlaceholder="Search fields..."
                    emptyMessage="No sortable fields found"
                    disabled={loading}
                    onChange={(updatedField) => {
                        if (loading) {
                            return;
                        }

                        if (updatedField === null) {
                            setInternalSort(CLEARED_SORT);
                            return;
                        }

                        setInternalSort({
                            field: updatedField,
                            direction:
                                internalSort.direction === SortDirections.none
                                    ? DEFAULT_SORT_DIRECTION
                                    : internalSort.direction,
                        });
                    }}
                />
            </div>

            <Button
                type="button"
                size="sm"
                variant="primaryLight"
                disabled={loading || selectedField === null}
                className="rounded-lg [&_svg]:size-2.5"
                onClick={() => {
                    if (loading || selectedField === null) {
                        return;
                    }

                    setInternalSort({
                        field: selectedField,
                        direction: isDescending
                            ? SortDirections.asc
                            : SortDirections.desc,
                    });
                }}
            >
                <span className="relative flex text-primary-dark/50">
                    <FontAwesomeIcon
                        icon={faSortAsc}
                        className={cn("size-2.5", {
                            "text-primary-dark":
                                internalSort.direction === SortDirections.asc,
                        })}
                    />
                    <FontAwesomeIcon
                        icon={faSortDesc}
                        className={cn("absolute size-2.5", {
                            "text-primary-dark":
                                internalSort.direction === SortDirections.desc,
                        })}
                    />
                </span>
                <span className="w-20">
                    {isDescending ? "Descending" : "Ascending"}
                </span>
            </Button>
        </div>
    );
}
